import React, { useState } from 'react';
import classNames from 'classnames';
import { ListingCard } from '../../../components';
import css from './GroupedSearchResults.module.css';

// renderSizes for 4-column grid (matches map-hidden list view)
const RENDER_SIZES =
  '(max-width: 549px) 100vw, (max-width: 767px) 50vw, (max-width: 1023px) 26vw, 22vw';

// ── Section definitions ───────────────────────────────────────────────────────

const SECTIONS = [
  {
    categoryIds: ['BoatRentals'],
    tabValue: 'boat-rental',
    filterType: 'boatRentals',
    eyebrow: 'TOP CATEGORY',
    heading: 'Boat Rentals',
    descriptor: 'Self-drive rentals and larger vessels',
  },
  {
    categoryIds: ['private_charters'],
    tabValue: 'captained-charter-private',
    filterType: 'privateCharters',
    eyebrow: 'BOOK THE WHOLE VESSEL',
    heading: 'Private Charters',
    descriptor: 'Exclusive-use vessels for your group',
  },
  {
    categoryIds: ['ticketed_charters', 'ticketed_experiences', 'private_experiences'],
    tabValue: 'experiences',
    filterType: 'experiences',
    eyebrow: 'PER-PERSON & GUIDED ACTIVITIES',
    heading: 'On-Water Experiences',
    descriptor: 'Tours, coaching, and shared outings',
  },
  {
    categoryIds: ['captains'],
    tabValue: 'captains-crew-transport',
    filterType: 'captains',
    eyebrow: 'CAPTAINS FOR HIRE',
    heading: 'Captains & Crew',
    descriptor: 'Licensed Captains, guides, and crew',
  },
  {
    categoryIds: ['dock-ramp-delivery', 'moorage-storage-parking', 'mechanics-and-technicians'],
    tabValue: 'has_any:captains-crew-transport,buoys-moorage-storage,marine-services',
    filterType: 'services',
    eyebrow: 'SUPPORT FOR BOATERS & OWNERS',
    heading: 'Local Services',
    descriptor: 'Launch help, moorage, and marine services',
  },
  {
    categoryIds: ['accessories-boating'],
    tabValue: 'accessory-rental',
    filterType: 'paddleGear',
    eyebrow: 'QUICK RENTALS & ADD-ONS',
    heading: 'Paddle & Gear Rentals',
    descriptor: 'Kayaks, boards, tubes, and watersports gear',
  },
];

// ── Private Charters filter constants ────────────────────────────────────────

const PRIVATE_CHARTER_ACTIVITY = [
  {
    l2Id: 'private-powerboat-charters',
    l2Label: 'Powerboat Charters',
    options: [
      { id: 'wakesurf-charter',          label: 'Wake / Surf Charter' },
      { id: 'tubing-watersports',        label: 'Tubing / Watersports' },
      { id: 'cruise-sightseeing',        label: 'Cruise / Sightseeing' },
      { id: 'dinner-sunset',             label: 'Dinner / Sunset Cruise' },
      { id: 'bachelor-bachelorette',     label: 'Bachelor / Bachelorette' },
      { id: 'scuba-snorkel',             label: 'SCUBA or Snorkel' },
      { id: 'specialty-niche-powerboat', label: 'Specialty / Niche' },
    ],
  },
  {
    l2Id: 'private-sailing-charters',
    l2Label: 'Sailing Charters',
    options: [
      { id: 'captain-only',    label: 'Captain-Only' },
      { id: 'captain-hostess', label: 'Captain + Hostess' },
      { id: 'fully-crewed',    label: 'Fully Crewed' },
    ],
  },
  {
    l2Id: 'private-fishing-charter',
    l2Label: 'Fishing Charters',
    options: [
      { id: 'inshore-nearshore',     label: 'Inshore / Nearshore' },
      { id: 'offshore-deep-sea',     label: 'Offshore / Deep Sea' },
      { id: 'sportfishing-big-game', label: 'Sport / Big Game' },
      { id: 'reef-bottom',           label: 'Reef / Bottom Fishing' },
      { id: 'specialty-fishing',     label: 'Specialty Fishing' },
      { id: 'expedition-fishing',    label: 'Expedition Fishing' },
    ],
  },
];

const CHARTER_DURATION_OPTIONS = [
  { id: 'half-hour',       label: '30 Min' },
  { id: 'one-hour',        label: '1 Hour' },
  { id: 'one-half-hour',   label: '1.5 Hours' },
  { id: 'two-hour',        label: '2 Hours' },
  { id: 'two-half-hour',   label: '2.5 Hours' },
  { id: 'three-hour',      label: '3 Hours' },
  { id: 'three-half-hour', label: '3.5 Hours' },
  { id: 'four-hour',       label: '4 Hours' },
  { id: 'five-hour',       label: '5 Hours' },
  { id: 'six-hour',        label: '6 Hours' },
  { id: 'seven-hour',      label: '7 Hours' },
  { id: 'eight-hour',      label: '8 Hours' },
];

// Returns the L2 parent ID for a given L3 activity ID (private charters).
const getL2ForL3Id = l3Id =>
  PRIVATE_CHARTER_ACTIVITY.find(g => g.options.some(o => o.id === l3Id))?.l2Id || null;

// ── Experience filter constants ───────────────────────────────────────────────

const EXPERIENCE_TYPE_OPTIONS = [
  { id: 'ticketed_charters',    label: 'Captained Public Charter' },
  { id: 'ticketed_experiences', label: 'Public Experience' },
  { id: 'private_experiences',  label: 'Private Experience' },
];

const ALL_EXPERIENCE_ACTIVITY_GROUPS = {
  ticketed_charters: [
    {
      l2Id: 'group-power-boat-charter',
      l2Label: 'Power Boat Tours',
      options: [
        { id: 'group-power-sightseeing-cruise', label: 'Sightseeing Cruise' },
        { id: 'group-power-sunset-cruise',      label: 'Sunset Cruise' },
        { id: 'group-power-wildlife-tour',      label: 'Wildlife & Nature Tour' },
        { id: 'group-power-party-cruise',       label: 'Party & Social Cruise' },
        { id: 'group-power-harbor-tour',        label: 'Harbor & City Tour' },
        { id: 'group-power-dining-cruise',      label: 'Dining Cruise' },
      ],
    },
    {
      l2Id: 'group-sailboat-charter',
      l2Label: 'Sailboat Tours',
      options: [
        { id: 'group-sailboat-sunset-sail', label: 'Sunset Sail' },
        { id: 'group-sailboat-day-cruise',  label: 'Day Sailing Cruise' },
        { id: 'group-sailboat-sightseeing', label: 'Sightseeing Sail' },
        { id: 'group-sailboat-wildlife',    label: 'Wildlife Sail' },
        { id: 'group-sailboat-learning',    label: 'Beginner Sailing Experience' },
        { id: 'group-sailboat-racing',      label: 'Racing / Performance Sail' },
      ],
    },
    {
      l2Id: 'group-fishing-charter',
      l2Label: 'Fishing Charters',
      options: [
        { id: 'group-deep-sea-fishing',   label: 'Deep Sea Fishing' },
        { id: 'group-inshore-fishing',    label: 'Inshore / Bay Fishing' },
        { id: 'group-freshwater-fishing', label: 'Freshwater Fishing' },
        { id: 'group-sportfishing',       label: 'Sportfishing / Big Game' },
        { id: 'group-guided-fishing',     label: 'Guided Fishing Trip' },
        { id: 'group-beginner-fishing',   label: 'Beginner Fishing Trip' },
      ],
    },
  ],
  ticketed_experiences: [
    {
      l2Id: 'group-water-experiences',
      l2Label: 'Water-Based Experiences',
      options: [
        { id: 'group-kayak-tour',   label: 'Kayak Tour' },
        { id: 'group-sup-tour',     label: 'Paddleboard Tour' },
        { id: 'group-snorkeling',   label: 'Snorkeling Trip' },
        { id: 'group-scuba-diving', label: 'Scuba Diving Trip' },
        { id: 'group-rafting',      label: 'River Rafting Trip' },
      ],
    },
    {
      l2Id: 'group-water-lessons',
      l2Label: 'Lessons & Coaching',
      options: [
        { id: 'group-wakesurf-lessons',    label: 'Wake Surf Lessons' },
        { id: 'group-watersports-lessons', label: 'Watersports Lessons' },
        { id: 'group-sailing-lessons',     label: 'Sailing Lessons' },
        { id: 'group-fishing-workshop',    label: 'Fishing Workshop' },
      ],
    },
    {
      l2Id: 'group-event-experiences',
      l2Label: 'Events & Group Activities',
      options: [
        { id: 'group-fireworks-cruise',  label: 'Fireworks Cruise' },
        { id: 'group-holiday-cruise',    label: 'Holiday Cruise' },
        { id: 'group-music-cruise',      label: 'Live Music Cruise' },
        { id: 'group-water-party-event', label: 'Party on the Water' },
      ],
    },
  ],
  private_experiences: [
    {
      l2Id: 'private-water-lessons',
      l2Label: 'Lessons & Coaching',
      options: [
        { id: 'private-wakesurf',             label: 'Wake Surf Lesson' },
        { id: 'private-watersports-coaching', label: 'Watersports Coaching' },
        { id: 'private-sailing-lessons',      label: 'Sailing Lesson' },
        { id: 'private-fishing-guide',        label: 'Guided Fishing Trip' },
      ],
    },
    {
      l2Id: 'private-water-experiences',
      l2Label: 'Water-Based Experiences',
      options: [
        { id: 'private-kayak-tour', label: 'Kayak Tour' },
        { id: 'private-sup-tour',   label: 'Paddleboard Tour' },
        { id: 'private-snorkeling', label: 'Snorkeling Trip' },
        { id: 'private-scuba',      label: 'Scuba Diving Trip' },
      ],
    },
    {
      l2Id: 'private-luxury-experiences',
      l2Label: 'Luxury & Hosted Experiences',
      options: [
        { id: 'private-sunset-experience', label: 'Sunset Experience' },
        { id: 'private-water-picnic',      label: 'Picnic on the Water' },
        { id: 'private-dock-and-dine',     label: 'Dock & Dine Experience' },
        { id: 'private-island-hopping',    label: 'Island Hopping' },
      ],
    },
  ],
};

// Flat list of all experience L3 options for label lookups.
const ALL_EXPERIENCE_L3_FLAT = Object.values(ALL_EXPERIENCE_ACTIVITY_GROUPS)
  .flat()
  .flatMap(g => g.options.map(o => ({ ...o, l2Id: g.l2Id })));

const getExpL2ForL3Id = l3Id =>
  ALL_EXPERIENCE_L3_FLAT.find(o => o.id === l3Id)?.l2Id || null;

// ── Boat Rentals filter constants ─────────────────────────────────────────────

const BOAT_TYPE_OPTIONS = [
  { id: 'wakesurf-boats',  label: 'Wake / Surf Boat' },
  { id: 'pontoon-tritoon', label: 'Pontoon' },
  { id: 'fishing-boats',   label: 'Fishing Boat' },
  { id: 'center-console',  label: 'Center Console' },
  { id: 'cabin-cruiser',   label: 'Cabin Cruiser' },
  { id: 'sailboats',       label: 'Sailboat' },
  { id: 'power-boats',     label: 'Other Powerboat' },
];

const CAPTAIN_PILL_OPTIONS = [
  { id: 'no-requirements',              label: 'No Captain Required' },
  { id: 'captain-optional-preapproved', label: 'Captain Option Available' },
  { id: 'captain-required-any',         label: 'Captain Required' },
];

const FULFILLMENT_PILL_OPTIONS = [
  { id: 'board-at-home-base', label: 'Board at Home Base' },
  { id: 'provider-delivers',  label: 'Provider Delivery' },
  { id: 'third-party-ok',     label: '3rd Party Delivery' },
  { id: 'customer-tow-away',  label: 'Customer Tow Away' },
];

// ── Captains filter constants ─────────────────────────────────────────────────

const CAPTAIN_ROLE_OPTIONS = [
  { id: 'licensed-captains',       label: 'Licensed Captain' },
  { id: 'boat-drivers-unlicensed', label: 'Boat Driver' },
  { id: 'guides',                  label: 'Guide' },
];

// ── Services filter constants ─────────────────────────────────────────────────

const SERVICES_DELIVERY_OPTIONS = [
  { id: 'dockdelivery',          label: 'Dock Delivery & Retrieval (On-Water)' },
  { id: 'ramplaunch',            label: 'Boat Ramp Launching & Retrieval (Trailering)' },
  { id: 'full-service-delivery', label: 'Full-Service Logistics' },
];

const SERVICES_MOORAGE_OPTIONS = [
  { id: 'mooring-buoy-for-rent', label: 'Mooring Buoy' },
  { id: 'slip',                  label: 'Slip' },
  { id: 'dock',                  label: 'Dock' },
  { id: 'boat-lift',             label: 'Boat Lift' },
];

const SERVICES_STORAGE_OPTIONS = [
  { id: 'drystack',             label: 'Dry Stack' },
  { id: 'enclosed-storage',     label: 'Enclosed Storage' },
  { id: 'boat-trailer-parking', label: 'Boat & Trailer Parking' },
  { id: 'off-season-storage',   label: 'Off-Season Storage' },
];

const SERVICES_MARINE_OPTIONS = [
  { id: 'mechanics',                     label: 'Mechanics' },
  { id: 'detailers',                     label: 'Detailing' },
  { id: 'fiberglass-gelcoat',            label: 'Fiberglass & Gelcoat' },
  { id: 'emergency-on-water-towing',     label: 'Emergency Towing' },
  { id: 'bottom-cleaning',               label: 'Bottom Cleaning' },
  { id: 'winterization-and-shrink-wrap', label: 'Winterization / Shrink Wrap' },
  { id: 'electronics-installation',      label: 'Electronics Installation' },
  { id: 'canvas-and-upholstery-repair',  label: 'Canvas & Upholstery' },
];

// ── Paddle & Gear filter constants ────────────────────────────────────────────

const PADDLE_GEAR_GROUPS = [
  {
    label: 'Towable / Watersports',
    options: [
      { id: 'tubes',             label: 'Tubes' },
      { id: 'wakeboards',        label: 'Wakeboards' },
      { id: 'wakesurfboards',    label: 'Wakesurf Boards' },
      { id: 'waterskis',         label: 'Waterskis' },
      { id: 'kneeboards',        label: 'Kneeboards' },
      { id: 'specialtytowables', label: 'Specialty Towables' },
    ],
  },
  {
    label: 'Human Powered',
    options: [
      { id: 'kayaks',        label: 'Kayaks' },
      { id: 'canoes',        label: 'Canoes' },
      { id: 'paddle-boards', label: 'Paddle Boards' },
      { id: 'row-boats',     label: 'Row Boats' },
      { id: 'pedal-boats',   label: 'Pedal Boats' },
      { id: 'river-rafts',   label: 'River Rafts' },
    ],
  },
  {
    label: 'Accessories',
    options: [
      { id: 'wetsuits',               label: 'Wetsuits' },
      { id: 'floatingislands',        label: 'Floating Islands' },
      { id: 'foilboards-non-motorized', label: 'Foil Boards' },
      { id: 'fishing-gear',           label: 'Fishing Gear' },
      { id: 'specialty-niche',        label: 'Specialty / Niche' },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const toHasAny = arr => {
  if (!arr || arr.length === 0) return null;
  return arr.length === 1 ? arr[0] : `has_any:${arr.join(',')}`;
};

const buildBoatTypeParams = selected => {
  if (selected.size === 0) return {};
  const hasSailboats = selected.has('sailboats');
  const hasGenericPower = selected.has('power-boats');
  const subtypeIds = ['wakesurf-boats', 'pontoon-tritoon', 'fishing-boats', 'center-console', 'cabin-cruiser']
    .filter(id => selected.has(id));
  const level2Parts = [];
  if (hasGenericPower || subtypeIds.length > 0) level2Parts.push('power-boats');
  if (hasSailboats) level2Parts.push('sailboats');
  const params = {};
  if (level2Parts.length > 0) {
    params.pub_categoryLevel2 = toHasAny(level2Parts);
  }
  if (subtypeIds.length > 0 && !hasSailboats) {
    params.pub_categoryLevel3 = toHasAny(subtypeIds);
  }
  return params;
};

// ── SectionFilterModal shell ──────────────────────────────────────────────────

const SectionFilterModal = ({ title, onClose, onClear, onShowResults, children }) => (
  <>
    <div className={css.sectionModalBackdrop} onClick={onClose} />
    <div className={css.sectionModalPanel} role="dialog" aria-modal="true">
      {/* Drag handle — mobile only */}
      <div className={css.sectionModalHandle} />

      {/* Mobile header: Clear | Title | Done */}
      <div className={css.sectionModalMobileHeader}>
        <button type="button" className={css.sectionModalResetBtn} onClick={onClear}>
          Clear
        </button>
        <span className={css.sectionModalTitle}>{title}</span>
        <button type="button" className={css.sectionModalDoneBtn} onClick={onClose}>
          Done
        </button>
      </div>

      {/* Desktop header: ✕ + Title */}
      <div className={css.sectionModalDesktopHeader}>
        <button
          type="button"
          className={css.sectionModalCloseBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
        <span className={css.sectionModalTitle}>{title}</span>
      </div>

      {/* Scrollable filter content */}
      <div className={css.sectionModalContent}>{children}</div>

      {/* Footer: Clear + Show results */}
      <div className={css.sectionModalFooter}>
        <button type="button" className={css.sectionModalClearBtn} onClick={onClear}>
          Clear
        </button>
        <button type="button" className={css.sectionModalShowBtn} onClick={onShowResults}>
          Show results
        </button>
      </div>
    </div>
  </>
);

// ── PillGroup — reusable pill row inside a modal section ──────────────────────

const PillGroup = ({ label, options, selected, onToggle }) => (
  <div className={css.modalFilterGroup}>
    {label && <div className={css.modalGroupLabel}>{label}</div>}
    <div className={css.modalPillRow}>
      {options.map(opt => (
        <button
          key={opt.id}
          type="button"
          className={classNames(css.modalPill, { [css.modalPillActive]: selected.has(opt.id) })}
          onClick={() => onToggle(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

// ── BoatRentalsFilterContent ──────────────────────────────────────────────────

const BoatRentalsFilterContent = ({ onChange }) => {
  const [boatTypes, setBoatTypes] = useState(() => new Set());
  const [captains, setCaptains] = useState(() => new Set());
  const [fulfillment, setFulfillment] = useState(() => new Set());

  const notify = (bt, cap, ful) => {
    const btParams = buildBoatTypeParams(bt);
    const capParams = cap.size > 0
      ? { pub_hired_captain_crew_requirements: toHasAny([...cap]) }
      : {};
    const fulParams = ful.size > 0
      ? { pub_pickup_options: toHasAny([...ful]) }
      : {};
    const allParams = { ...btParams, ...capParams, ...fulParams };
    const groupCount = (bt.size > 0 ? 1 : 0) + (cap.size > 0 ? 1 : 0) + (ful.size > 0 ? 1 : 0);
    if (Object.keys(allParams).length > 0) {
      onChange({ pub_categoryLevel1: 'BoatRentals', ...allParams }, groupCount);
    } else {
      onChange(null, 0);
    }
  };

  const toggle = (set, setter, id, others) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
    return next;
  };

  return (
    <>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Boat Type</div>
        <PillGroup
          options={BOAT_TYPE_OPTIONS}
          selected={boatTypes}
          onToggle={id => {
            const next = toggle(boatTypes, setBoatTypes, id);
            notify(next, captains, fulfillment);
          }}
        />
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Captain Option</div>
        <PillGroup
          options={CAPTAIN_PILL_OPTIONS}
          selected={captains}
          onToggle={id => {
            const next = toggle(captains, setCaptains, id);
            notify(boatTypes, next, fulfillment);
          }}
        />
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Fulfillment</div>
        <PillGroup
          options={FULFILLMENT_PILL_OPTIONS}
          selected={fulfillment}
          onToggle={id => {
            const next = toggle(fulfillment, setFulfillment, id);
            notify(boatTypes, captains, next);
          }}
        />
      </div>
    </>
  );
};

// ── PrivateCharterFilterContent ───────────────────────────────────────────────

const PrivateCharterFilterContent = ({ onChange }) => {
  const [activities, setActivities] = useState(() => new Set());
  const [durations, setDurations] = useState(() => new Set());

  const notify = (act, dur) => {
    const params = {};
    if (act.size > 0) {
      const l3Ids = [...act];
      const l2Ids = [...new Set(l3Ids.map(getL2ForL3Id).filter(Boolean))];
      params.pub_categoryLevel2 = toHasAny(l2Ids);
      params.pub_categoryLevel3 = toHasAny(l3Ids);
    }
    if (dur.size > 0) {
      params.pub_available_slot_durations = toHasAny([...dur]);
    }
    const groupCount = (act.size > 0 ? 1 : 0) + (dur.size > 0 ? 1 : 0);
    if (Object.keys(params).length > 0) {
      onChange({ pub_categoryLevel1: 'private_charters', ...params }, groupCount);
    } else {
      onChange(null, 0);
    }
  };

  const toggleAct = id => {
    const next = new Set(activities);
    next.has(id) ? next.delete(id) : next.add(id);
    setActivities(next);
    notify(next, durations);
  };

  const toggleDur = id => {
    const next = new Set(durations);
    next.has(id) ? next.delete(id) : next.add(id);
    setDurations(next);
    notify(activities, next);
  };

  return (
    <>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Activity</div>
        {PRIVATE_CHARTER_ACTIVITY.map(group => (
          <PillGroup
            key={group.l2Id}
            label={group.l2Label}
            options={group.options}
            selected={activities}
            onToggle={toggleAct}
          />
        ))}
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Duration</div>
        <PillGroup
          options={CHARTER_DURATION_OPTIONS}
          selected={durations}
          onToggle={toggleDur}
        />
      </div>
    </>
  );
};

// ── ExperienceFilterContent ───────────────────────────────────────────────────

const ExperienceFilterContent = ({ onChange }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [activities, setActivities] = useState(() => new Set());
  const [durations, setDurations] = useState(() => new Set());

  const notify = (type, act, dur) => {
    const hasAny = type || act.size > 0 || dur.size > 0;
    if (!hasAny) { onChange(null, 0); return; }

    const params = {};
    params.pub_categoryLevel1 = type
      ? type
      : 'has_any:ticketed_charters,ticketed_experiences,private_experiences';
    if (act.size > 0) {
      const l3Ids = [...act];
      const l2Ids = [...new Set(l3Ids.map(getExpL2ForL3Id).filter(Boolean))];
      params.pub_categoryLevel2 = toHasAny(l2Ids);
      params.pub_categoryLevel3 = toHasAny(l3Ids);
    }
    if (dur.size > 0) {
      params.pub_available_slot_durations = toHasAny([...dur]);
    }
    const groupCount = (type ? 1 : 0) + (act.size > 0 ? 1 : 0) + (dur.size > 0 ? 1 : 0);
    onChange(params, groupCount);
  };

  const handleTypeToggle = id => {
    const nextType = selectedType === id ? null : id;
    // Drop activities not valid for the new type
    let nextAct = activities;
    if (nextType !== selectedType && activities.size > 0) {
      const validGroups = nextType
        ? ALL_EXPERIENCE_ACTIVITY_GROUPS[nextType] || []
        : ALL_EXPERIENCE_ACTIVITY_GROUPS['ticketed_charters'];
      const validIds = new Set(validGroups.flatMap(g => g.options.map(o => o.id)));
      nextAct = new Set([...activities].filter(id => validIds.has(id)));
    }
    setSelectedType(nextType);
    setActivities(nextAct);
    notify(nextType, nextAct, durations);
  };

  const toggleAct = id => {
    const next = new Set(activities);
    next.has(id) ? next.delete(id) : next.add(id);
    setActivities(next);
    notify(selectedType, next, durations);
  };

  const toggleDur = id => {
    const next = new Set(durations);
    next.has(id) ? next.delete(id) : next.add(id);
    setDurations(next);
    notify(selectedType, activities, next);
  };

  // Activity groups depend on selected type (default = ticketed_charters groups)
  const activeGroups = selectedType
    ? ALL_EXPERIENCE_ACTIVITY_GROUPS[selectedType] || []
    : ALL_EXPERIENCE_ACTIVITY_GROUPS['ticketed_charters'];

  return (
    <>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Type</div>
        <div className={css.modalPillRow}>
          {EXPERIENCE_TYPE_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              className={classNames(css.modalPill, { [css.modalPillActive]: selectedType === opt.id })}
              onClick={() => handleTypeToggle(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Activity</div>
        {activeGroups.map(group => (
          <PillGroup
            key={group.l2Id}
            label={group.l2Label}
            options={group.options}
            selected={activities}
            onToggle={toggleAct}
          />
        ))}
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Duration</div>
        <PillGroup
          options={CHARTER_DURATION_OPTIONS}
          selected={durations}
          onToggle={toggleDur}
        />
      </div>
    </>
  );
};

// ── CaptainsFilterContent ─────────────────────────────────────────────────────

const CaptainsFilterContent = ({ onChange }) => {
  const [roles, setRoles] = useState(() => new Set());

  const notify = next => {
    if (next.size === 0) { onChange(null, 0); return; }
    onChange({
      pub_categoryLevel1: 'captains',
      pub_categoryLevel2: toHasAny([...next]),
    }, 1);
  };

  const toggleRole = id => {
    const next = new Set(roles);
    next.has(id) ? next.delete(id) : next.add(id);
    setRoles(next);
    notify(next);
  };

  return (
    <div className={css.sectionModalSection}>
      <div className={css.sectionModalSectionTitle}>Role</div>
      <PillGroup options={CAPTAIN_ROLE_OPTIONS} selected={roles} onToggle={toggleRole} />
    </div>
  );
};

// ── ServicesFilterContent ─────────────────────────────────────────────────────

const ServicesFilterContent = ({ onChange }) => {
  const [delivery, setDelivery] = useState(() => new Set());
  const [moorage, setMoorage] = useState(() => new Set());
  const [marine, setMarine] = useState(() => new Set());

  const notify = (del, moor, mar) => {
    const activeCats = [
      del.size > 0 && 'dock-ramp-delivery',
      moor.size > 0 && 'moorage-storage-parking',
      mar.size > 0 && 'mechanics-and-technicians',
    ].filter(Boolean);

    if (activeCats.length === 0) { onChange(null, 0); return; }

    const params = { pub_categoryLevel1: toHasAny(activeCats) };
    if (activeCats.length === 1) {
      if (del.size > 0) params.pub_categoryLevel2 = toHasAny([...del]);
      else if (moor.size > 0) params.pub_categoryLevel3 = toHasAny([...moor]);
      else if (mar.size > 0) params.pub_categoryLevel2 = toHasAny([...mar]);
    }
    const groupCount = (del.size > 0 ? 1 : 0) + (moor.size > 0 ? 1 : 0) + (mar.size > 0 ? 1 : 0);
    onChange(params, groupCount);
  };

  const toggleDel = id => {
    const next = new Set(delivery);
    next.has(id) ? next.delete(id) : next.add(id);
    setDelivery(next);
    notify(next, moorage, marine);
  };

  const toggleMoor = id => {
    const next = new Set(moorage);
    next.has(id) ? next.delete(id) : next.add(id);
    setMoorage(next);
    notify(delivery, next, marine);
  };

  const toggleMar = id => {
    const next = new Set(marine);
    next.has(id) ? next.delete(id) : next.add(id);
    setMarine(next);
    notify(delivery, moorage, next);
  };

  return (
    <>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Boat Delivery & Logistics</div>
        <PillGroup options={SERVICES_DELIVERY_OPTIONS} selected={delivery} onToggle={toggleDel} />
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Moorage, Storage & Parking</div>
        <PillGroup label="Moorage (On the Water)" options={SERVICES_MOORAGE_OPTIONS} selected={moorage} onToggle={toggleMoor} />
        <PillGroup label="Storage (On Land)" options={SERVICES_STORAGE_OPTIONS} selected={moorage} onToggle={toggleMoor} />
      </div>
      <div className={css.sectionModalSection}>
        <div className={css.sectionModalSectionTitle}>Marine Services</div>
        <PillGroup options={SERVICES_MARINE_OPTIONS} selected={marine} onToggle={toggleMar} />
      </div>
    </>
  );
};

// ── PaddleGearFilterContent ───────────────────────────────────────────────────

const PaddleGearFilterContent = ({ onChange }) => {
  const [selected, setSelected] = useState(() => new Set());

  const notify = next => {
    if (next.size === 0) { onChange(null, 0); return; }
    onChange({
      pub_categoryLevel1: 'accessories-boating',
      pub_categoryLevel2: toHasAny([...next]),
    }, 1);
  };

  const toggle = id => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
    notify(next);
  };

  return (
    <>
      {PADDLE_GEAR_GROUPS.map(group => (
        <div key={group.label} className={css.sectionModalSection}>
          <div className={css.sectionModalSectionTitle}>{group.label}</div>
          <PillGroup options={group.options} selected={selected} onToggle={toggle} />
        </div>
      ))}
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Grouped search results feed shown on the "All" tab and when category chips are selected.
 */
const GroupedSearchResults = ({
  listings = [],
  setActiveListing,
  onViewAll,
  onBack,
  activeCategoryIds = [],
  expandedTabValue = null,
  isMapHidden,
  isMobile,
  onSearchSection,
  onClearSection,
  sectionListingsMap = {},
  urlQueryParams,
}) => {
  const [openModalTabValue, setOpenModalTabValue] = useState(null);
  const [sectionAppliedParams, setSectionAppliedParams] = useState({});
  const [pendingParams, setPendingParams] = useState(null);
  const [pendingGroupCount, setPendingGroupCount] = useState(0);

  const gridClasses = classNames(css.cardGrid, { [css.cardGridExpanded]: isMapHidden });

  const visibleSections = activeCategoryIds.length > 0
    ? SECTIONS.filter(s => s.categoryIds.some(id => activeCategoryIds.includes(id)))
    : SECTIONS;

  const getGlobalSectionListings = section =>
    listings.filter(l => {
      const { categoryLevel1 } = l?.attributes?.publicData || {};
      return section.categoryIds.includes(categoryLevel1);
    });

  // Extract shared search params (location, dates, price, sort) from the current URL.
  const extractLocationParams = () => {
    const { bounds, origin, dates, price, sort } = urlQueryParams || {};
    const params = {};
    if (bounds) params.bounds = bounds;
    if (origin) params.origin = origin;
    if (dates) params.dates = dates;
    if (price) params.price = price;
    if (sort) params.sort = sort;
    return params;
  };

  // ── Modal state management ────────────────────────────────────────────────

  const openModal = tabValue => {
    setPendingParams(null);
    setPendingGroupCount(0);
    setOpenModalTabValue(tabValue);
  };

  const closeModal = () => setOpenModalTabValue(null);

  const handleFilterChange = (params, count) => {
    setPendingParams(params || null);
    setPendingGroupCount(count || 0);
  };

  const handleShowResults = () => {
    const tabValue = openModalTabValue;
    if (!pendingParams || Object.keys(pendingParams).length === 0) {
      handleClearModal();
      return;
    }
    setSectionAppliedParams(prev => ({
      ...prev,
      [tabValue]: { params: pendingParams, groupCount: pendingGroupCount },
    }));
    onSearchSection(tabValue, { ...extractLocationParams(), ...pendingParams });
    closeModal();
  };

  const handleClearModal = () => {
    const tabValue = openModalTabValue;
    setSectionAppliedParams(prev => {
      const next = { ...prev };
      delete next[tabValue];
      return next;
    });
    if (onClearSection) onClearSection(tabValue);
    closeModal();
  };

  // ── Per-section helpers ───────────────────────────────────────────────────

  const getFilterCount = tabValue =>
    sectionAppliedParams[tabValue]?.groupCount || 0;

  const renderFilterBtn = tabValue => {
    const count = getFilterCount(tabValue);
    const isActive = count > 0;
    return (
      <button
        type="button"
        className={classNames(css.sectionFilterIconBtn, { [css.sectionFilterIconBtnActive]: isActive })}
        onClick={() => openModal(tabValue)}
        aria-label={isActive ? `Filters (${count} active)` : 'Filters'}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6"/>
          <line x1="4" y1="12" x2="20" y2="12"/>
          <line x1="4" y1="18" x2="20" y2="18"/>
          <circle cx="8" cy="6" r="2" fill="currentColor" stroke="none"/>
          <circle cx="16" cy="12" r="2" fill="currentColor" stroke="none"/>
          <circle cx="10" cy="18" r="2" fill="currentColor" stroke="none"/>
        </svg>
        {isActive && <span className={css.sectionFilterDot} aria-hidden="true" />}
      </button>
    );
  };

  const renderFilterContent = () => {
    const section = SECTIONS.find(s => s.tabValue === openModalTabValue);
    if (!section) return null;
    const key = openModalTabValue;
    const props = { onChange: handleFilterChange };
    switch (section.filterType) {
      case 'boatRentals':      return <BoatRentalsFilterContent key={key} {...props} />;
      case 'privateCharters':  return <PrivateCharterFilterContent key={key} {...props} />;
      case 'experiences':      return <ExperienceFilterContent key={key} {...props} />;
      case 'captains':         return <CaptainsFilterContent key={key} {...props} />;
      case 'services':         return <ServicesFilterContent key={key} {...props} />;
      case 'paddleGear':       return <PaddleGearFilterContent key={key} {...props} />;
      default:                 return null;
    }
  };

  const resolveSectionData = section => {
    const categoryAnchor = section.categoryIds[0];
    const globalListings = getGlobalSectionListings(section);
    const sectionResult = sectionListingsMap[section.tabValue];
    const hasSectionSearch = !!sectionResult;
    const isSectionLoading = sectionResult?.inProgress || false;
    const sectionListings = hasSectionSearch && !isSectionLoading
      ? (sectionResult.listings || [])
      : globalListings;
    const shouldHide = globalListings.length === 0 && !hasSectionSearch;
    const isFilteredEmpty = hasSectionSearch && !isSectionLoading && sectionListings.length === 0;
    return { categoryAnchor, sectionListings, shouldHide, isFilteredEmpty };
  };

  const openSection = SECTIONS.find(s => s.tabValue === openModalTabValue);

  // ── Mobile layout ─────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div className={css.root}>
        {openModalTabValue && (
          <SectionFilterModal
            title={openSection?.heading || 'Filters'}
            onClose={closeModal}
            onClear={handleClearModal}
            onShowResults={handleShowResults}
          >
            {renderFilterContent()}
          </SectionFilterModal>
        )}
        {visibleSections.map(section => {
          const { sectionListings, shouldHide, isFilteredEmpty } = resolveSectionData(section);
          if (shouldHide) return null;
          const isExpanded = expandedTabValue === section.tabValue || visibleSections.length === 1;

          if (isExpanded) {
            return (
              <div key={section.tabValue} className={css.mobileExpandedSection}>
                <div className={css.mobileExpandedHeader}>
                  <span className={css.mobileRowLabel}>{section.heading}</span>
                  <div className={css.mobileRowActions}>
                    {renderFilterBtn(section.tabValue)}
                    <button type="button" className={css.mobileBack} onClick={onBack}>
                      ← Back
                    </button>
                  </div>
                </div>
                <p className={css.descriptor}>{section.descriptor}</p>
                {isFilteredEmpty ? (
                  <p className={css.noFilteredResults}>No exact matches</p>
                ) : (
                  <ul className={css.mobileExpandedGrid}>
                    {sectionListings.map(listing => (
                      <li key={listing.id.uuid} className={css.cardItem}>
                        <div className={css.cardWrapper}>
                          <ListingCard
                            className={css.card}
                            listing={listing}
                            renderSizes="50vw"
                            setActiveListing={setActiveListing}
                            hideMetadata
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          }

          return (
            <div key={section.tabValue} className={css.mobileRow}>
              <div className={css.mobileRowHeader}>
                <span className={css.mobileRowLabel}>{section.heading}</span>
                <div className={css.mobileRowActions}>
                  {renderFilterBtn(section.tabValue)}
                  <button
                    type="button"
                    className={css.mobileViewAll}
                    onClick={() => onViewAll(section)}
                  >
                    View all →
                  </button>
                </div>
              </div>
              {isFilteredEmpty ? (
                <p className={css.noFilteredResults}>No exact matches</p>
              ) : (
                <ul className={css.mobileScrollStrip}>
                  {sectionListings.slice(0, 8).map(listing => (
                    <li key={listing.id.uuid} className={css.mobileScrollCard}>
                      <div className={css.cardWrapper}>
                        <ListingCard
                          className={css.card}
                          listing={listing}
                          renderSizes="60vw"
                          setActiveListing={setActiveListing}
                          hideMetadata
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────

  return (
    <div className={css.root}>
      {openModalTabValue && (
        <SectionFilterModal
          title={openSection?.heading || 'Filters'}
          onClose={closeModal}
          onClear={handleClearModal}
          onShowResults={handleShowResults}
        >
          {renderFilterContent()}
        </SectionFilterModal>
      )}
      <div className={css.sections}>
        {visibleSections.map(section => {
          const { sectionListings, shouldHide, isFilteredEmpty } = resolveSectionData(section);
          if (shouldHide) return null;

          const isExpanded = expandedTabValue === section.tabValue || visibleSections.length === 1;
          const colCount = isMapHidden ? 4 : 3;
          const displayListings = isExpanded ? sectionListings : sectionListings.slice(0, colCount);

          return (
            <div key={section.tabValue} className={css.section}>
              {/* Header */}
              <div className={css.sectionHeader}>
                <div className={css.sectionMeta}>
                  <p className={css.eyebrow}>{section.eyebrow}</p>
                  <h2 className={css.sectionHeading}>{section.heading}</h2>
                  <p className={css.descriptor}>{section.descriptor}</p>
                </div>
                <div className={css.sectionHeaderActions}>
                  {renderFilterBtn(section.tabValue)}
                  {isExpanded ? (
                    <button type="button" className={css.viewAll} onClick={onBack}>
                      ← Back
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={css.viewAll}
                      onClick={() => onViewAll(section)}
                    >
                      View all →
                    </button>
                  )}
                </div>
              </div>

              {/* Card grid or no-results message */}
              {isFilteredEmpty ? (
                <p className={css.noFilteredResults}>No exact matches</p>
              ) : (
                <ul className={gridClasses}>
                  {displayListings.map(listing => (
                    <li key={listing.id.uuid} className={css.cardItem}>
                      <div className={css.cardWrapper}>
                        <ListingCard
                          className={css.card}
                          listing={listing}
                          renderSizes={RENDER_SIZES}
                          setActiveListing={setActiveListing}
                          hideMetadata
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupedSearchResults;
